// Peças comuns das telas "Envio de Contas" (faturamento) e "Pendência de Orçamentos" (setor de orçamentos):
// carregamento de /api/prorrogacoes, cores das situações, cartões, faixa de prazos do mês e a fala em horários fixos.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { supabase } from '../supabase';
import { buscarIW } from '../iw/apiIW';
import { speak } from '../utils/speechUtils';

export const P = { bg: '#f8fafc', white: '#fff', border: '#e2e8f0', text: '#1e293b', muted: '#64748b', primary: '#6366f1' };
export const COR_UNIDADE = { Vitalcare: '#2a78d6', HomeBaby: '#eb6834', Outra: '#1baf7a' };
export const UNIDADES = ['Vitalcare', 'HomeBaby', 'Outra'];

// 🔴 atrasado / precisa de ação · 🟡 vence em breve · 🟢 no prazo · ⚪ sem prazo · ✅ concluído
export const SIT = {
  atrasado: { l: 'Atrasado', emoji: '🔴', cor: '#dc2626', fundo: '#fee2e2', texto: '#991b1b', ordem: 0 },
  acao: { l: 'Precisa de ação', emoji: '🔴', cor: '#dc2626', fundo: '#fee2e2', texto: '#991b1b', ordem: 1 },
  vence: { l: 'Vence em breve', emoji: '🟡', cor: '#d97706', fundo: '#fef3c7', texto: '#92400e', ordem: 2 },
  no_prazo: { l: 'No prazo', emoji: '🟢', cor: '#16a34a', fundo: '#dcfce7', texto: '#166534', ordem: 3 },
  sem_prazo: { l: 'Sem prazo', emoji: '⚪', cor: '#94a3b8', fundo: '#f1f5f9', texto: '#475569', ordem: 4 },
  ok: { l: 'Concluído', emoji: '✅', cor: '#16a34a', fundo: '#dcfce7', texto: '#166534', ordem: 5 },
};
// Códigos do IW, os mesmos nas duas autorizações (conferidos na tela do IW em 21/10/2026).
export const AUTORIZACAO = { 1: ['Aguardando aprovação', '#fef3c7', '#92400e'], 2: ['Aprovado', '#dcfce7', '#166534'], 3: ['Reprovado', '#fee2e2', '#991b1b'] };

export const NOMES_MES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const NOMES_MES_LONGO = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
export const fmtMes = m => (m ? `${NOMES_MES[Number(m.slice(5, 7)) - 1]}/${m.slice(2, 4)}` : '—');
export const mesPorExtenso = m => (m ? NOMES_MES_LONGO[Number(m.slice(5, 7)) - 1] : '');
export const fmtData = iso => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '—');
export const fmtDataHora = iso => (iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
export const chaveTexto = s => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
export const chip = (fundo, cor) => ({ fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: fundo, color: cor, whiteSpace: 'nowrap', display: 'inline-block' });
export const plural = (n, um, varios) => `${n} ${n === 1 ? um : varios}`;

/** Texto da situação de uma linha ("Atrasado 3 dias", "Vence hoje", "Prazo 20/11"). */
export function textoSituacao(s, prazo) {
  if (!s) return '';
  if (s.situacao === 'atrasado') return `Atrasado ${plural(-s.dias, 'dia', 'dias')}`;
  if (s.situacao === 'vence' && s.dias !== null && s.dias !== undefined) return s.dias === 0 ? 'Vence hoje' : s.dias === 1 ? 'Vence amanhã' : `Vence em ${s.dias} dias`;
  if (s.situacao === 'no_prazo' && prazo) return `Prazo ${fmtData(prazo)}`;
  return SIT[s.situacao]?.l || s.situacao;
}

export function Situacao({ s, texto }) {
  const m = SIT[s?.situacao] || SIT.sem_prazo;
  return <span style={chip(m.fundo, m.texto)}>{m.emoji} {texto}</span>;
}

export function Autorizacao({ codigo }) {
  if (!codigo) return <span style={{ color: P.muted, fontSize: '0.75rem' }}>—</span>;
  const [l, f, c] = AUTORIZACAO[codigo] || [`código ${codigo}`, '#f1f5f9', '#475569'];
  return <span style={chip(f, c)}>{l}</span>;
}

/** Cartão de total no topo; clicável vira filtro. */
export function Cartao({ rotulo, valor, sub, cor = P.text, ativo, aoClicar, grande }) {
  return (
    <div onClick={aoClicar} role={aoClicar ? 'button' : undefined} aria-pressed={aoClicar ? !!ativo : undefined}
      style={{ background: ativo ? '#eef2ff' : P.white, border: `${ativo ? 2 : 1}px solid ${ativo ? P.primary : P.border}`, borderRadius: 12, padding: grande ? '12px 16px' : '10px 14px', minWidth: 150, flex: '1 1 150px', cursor: aoClicar ? 'pointer' : 'default' }}>
      <div style={{ fontSize: '0.74rem', color: P.muted, fontWeight: 700 }}>{rotulo}</div>
      <div style={{ fontSize: grande ? '2rem' : '1.6rem', fontWeight: 800, color: cor, lineHeight: 1.15 }}>{valor}</div>
      {sub && <div style={{ fontSize: '0.72rem', color: P.muted }}>{sub}</div>}
    </div>
  );
}

/** Dados de /api/prorrogacoes; recarrega a cada 3 min e ao voltar para a aba depois de 1 min fora. */
export function useProrrogacoes() {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(true);
  const ultima = useRef(0);
  const carregar = useCallback(async () => {
    setCarregando(true);
    try { setDados(await buscarIW('prorrogacoes?acao=listar')); setErro(''); ultima.current = Date.now(); } catch (e) { setErro(e.message); }
    setCarregando(false);
  }, []);
  useEffect(() => {
    carregar();
    const t = setInterval(carregar, 3 * 60 * 1000);
    const aoVoltar = () => { if (!document.hidden && Date.now() - ultima.current > 60 * 1000) carregar(); };
    document.addEventListener('visibilitychange', aoVoltar);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', aoVoltar); };
  }, [carregar]);
  return { dados, erro, carregando, carregar };
}

export async function postarProrrogacoes(acao, corpo) {
  const { data: { session } } = await supabase.auth.getSession();
  const r = await fetch(`/api/prorrogacoes?acao=${acao}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${session?.access_token || ''}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(corpo),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `Erro ${r.status}`);
  return j;
}

const horaBR = () => new Date().toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' });
const dataBR = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });

/**
 * Fala o resumo da tela nos horários configurados (ex.: 10:00, 12:00, 14:00, 16:00). O navegador só deixa
 * falar depois de um clique na página — por isso o botão "Ativar voz" (na TV, uma vez ao abrir a tela).
 */
export function ControleVoz({ chave, horarios = [], resumo }) {
  const [ativa, setAtiva] = useState(false);
  const resumoRef = useRef(resumo);
  resumoRef.current = resumo;
  const proximo = horarios.find(h => h > horaBR()) || horarios[0];

  useEffect(() => {
    if (!ativa || !horarios.length) return undefined;
    const t = setInterval(() => {
      const h = horaBR();
      if (!horarios.includes(h)) return;
      const marca = `fala_${chave}_${dataBR()}_${h}`;
      try { if (sessionStorage.getItem(marca)) return; sessionStorage.setItem(marca, '1'); } catch { /* segue */ }
      speak(resumoRef.current());
    }, 15 * 1000);
    return () => clearInterval(t);
  }, [ativa, horarios, chave]);

  const botao = { border: `1px solid ${P.border}`, background: P.white, borderRadius: 8, padding: '6px 12px', cursor: 'pointer', display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: '0.8rem' };
  if (!ativa) {
    return (
      <button onClick={() => { setAtiva(true); speak(`Voz ativada.${proximo ? ` Próximo aviso às ${proximo.replace(':00', ' horas')}.` : ''}`); }}
        style={{ ...botao, background: '#fef3c7', borderColor: '#fcd34d', color: '#92400e', fontWeight: 700 }} title="O navegador só deixa a tela falar depois de um clique">
        <VolumeX size={14} /> Ativar voz {horarios.length ? `(${horarios.join(', ')})` : ''}
      </button>
    );
  }
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <span style={{ fontSize: '0.76rem', color: '#166534', fontWeight: 700 }}>🔊 Voz ligada{proximo ? ` · próximo aviso ${proximo}` : ''}</span>
      <button onClick={() => speak(resumoRef.current())} style={botao}><Volume2 size={14} /> Ouvir agora</button>
    </span>
  );
}

/** Convênios com mais itens numa lista → " Amil 5, Bradesco 4 e Unimed Brasil 3" (para a fala). */
export function porConvenioFalado(linhas, max = 3) {
  const g = {};
  linhas.forEach(l => { const k = l.convenio || l.operadora; g[k] = (g[k] || 0) + 1; });
  const top = Object.entries(g).sort((a, b) => b[1] - a[1]).slice(0, max).map(([k, n]) => `${k} ${n}`);
  if (!top.length) return '';
  return `: ${top.length > 1 ? `${top.slice(0, -1).join(', ')} e ${top[top.length - 1]}` : top[0]}`;
}

/** Faixa "Prazos de novembro": um chip por convênio com as datas de aprovação e/ou envio do mês. */
export function FaixaPrazos({ calendario, hoje, mesAtual, foco }) {
  const datas = c => (foco === 'orcamento' ? [c.aprovacao] : [c.envioAte || c.aprovacao]).filter(Boolean);
  const itens = (calendario || [])
    .filter(c => (foco === 'orcamento' ? c.aprovacao : !c.foraDoEnvio && c.envioAte))
    .sort((a, b) => (datas(a)[0] || '').localeCompare(datas(b)[0] || ''));
  if (!itens.length) return null;
  return (
    <div style={{ background: P.white, border: `1px solid ${P.border}`, borderRadius: 12, padding: '8px 12px', display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
      <span style={{ fontSize: '0.8rem', fontWeight: 800, color: P.text, marginRight: 4 }}>📅 Prazos de {mesPorExtenso(mesAtual)}:</span>
      {itens.map(c => {
        const d = datas(c)[0];
        const passou = d < hoje;
        const perto = !passou && (Date.parse(d) - Date.parse(hoje)) / 864e5 <= 2;
        const envio = c.envioDe && c.envioDe !== c.envioAte ? `${fmtData(c.envioDe)} a ${fmtData(c.envioAte)}` : `até ${fmtData(c.envioAte)}`;
        const texto = foco === 'orcamento' ? `aprovar até ${fmtData(c.aprovacao)}` : `enviar ${envio}`;
        return (
          <span key={c.nome} title={`${c.nome} — ${c.referencia === 'subsequente' ? 'prorrogação do mês seguinte' : 'prorrogação do próprio mês'}${c.corte ? ` · corte dos profissionais: ${c.corte}` : ''}${c.obs ? ` · ${c.obs}` : ''}`}
            style={{ fontSize: '0.74rem', padding: '3px 9px', borderRadius: 999, border: `1px solid ${perto ? '#fcd34d' : P.border}`, background: passou ? '#f1f5f9' : perto ? '#fef3c7' : P.white, color: passou ? '#94a3b8' : P.text, textDecoration: passou ? 'line-through' : 'none', whiteSpace: 'nowrap' }}>
            <strong>{c.nome}</strong> · {texto}{c.periodo ? ` (${fmtMes(c.periodo)})` : ''}
          </span>
        );
      })}
    </div>
  );
}

/** Botão de tela cheia (para TV). */
export function botaoTelaCheia(ref) {
  const el = ref.current;
  if (!el) return;
  if (document.fullscreenElement) document.exitFullscreen?.();
  else el.requestFullscreen?.();
}
